import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Sensor, SensorReading } from '@/types';
import { sensorsService } from '@/services';
import { 
  History, 
  Thermometer, 
  Droplets, 
  Zap,
  Calendar,
  TrendingUp,
  TrendingDown,
  Minus,
  AlertCircle
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
} from 'recharts';

interface SensorHistoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sensor: Sensor | null;
}

// Helper para convertir ApiSensorReading a SensorReading
function apiReadingToFrontend(apiReading: any): SensorReading {
  return {
    id: apiReading.id,
    sensorId: apiReading.sensorId,
    temperature: apiReading.temperature,
    humidity: apiReading.humidity,
    conductivity: apiReading.conductivity,
    timestamp: new Date(apiReading.timestamp),
  };
}

export function SensorHistoryDialog({
  open,
  onOpenChange,
  sensor,
}: SensorHistoryDialogProps) {
  const [period, setPeriod] = useState<'24h' | '7d'>('24h');

  // Calcular fechas para las queries
  const now = new Date();
  const today = now.toISOString().split('T')[0];
  const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString().split('T')[0];

  // Query para lecturas de las últimas 24 horas
  const { data: readings24hData, isLoading: isLoading24h } = useQuery({
    queryKey: ['sensor-readings', sensor?.id, '24h'],
    queryFn: async () => {
      if (!sensor) return null;
      const response = await sensorsService.getReadings(sensor.id, {
        start: yesterday,
        end: today,
      });
      if (!response.success) {
        throw new Error(response.error?.message || 'Error al cargar lecturas');
      }
      return response.data || [];
    },
    enabled: !!sensor && open && period === '24h',
  });

  // Query para datos agregados de los últimos 7 días
  const { data: soilDataResponse, isLoading: isLoading7d } = useQuery({
    queryKey: ['sensor-soil-data', sensor?.id, '7d'],
    queryFn: async () => {
      if (!sensor) return null;
      const response = await sensorsService.getSoilData(sensor.id, {
        start: sevenDaysAgo,
        end: today,
      });
      if (!response.success) {
        throw new Error(response.error?.message || 'Error al cargar datos agregados');
      }
      return response.data?.soilData || [];
    },
    enabled: !!sensor && open && period === '7d',
  });

  // Convertir lecturas a formato frontend
  const hourlyData = useMemo(() => {
    if (!readings24hData) return [];
    return readings24hData.map(apiReadingToFrontend).sort((a, b) => 
      a.timestamp.getTime() - b.timestamp.getTime()
    );
  }, [readings24hData]);

  // Convertir datos agregados a formato para gráficos
  const weeklyData = useMemo(() => {
    if (!soilDataResponse) return [];
    return soilDataResponse.map(day => ({
      date: new Date(day.date).toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric' }),
      tempMax: day.soilTempMax,
      tempMin: day.soilTempMin,
      tempAvg: day.soilTempAvg,
      humMax: day.soilHumMax,
      humMin: day.soilHumMin,
      humAvg: day.soilHumAvg,
    }));
  }, [soilDataResponse]);

  const chartData = useMemo(() => {
    if (period === '24h') {
      return hourlyData.map(r => ({
        time: r.timestamp.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }),
        temperatura: r.temperature,
        humedad: r.humidity,
        conductividad: r.conductivity || 0,
      }));
    }
    return weeklyData;
  }, [period, hourlyData, weeklyData]);

  const isLoading = period === '24h' ? isLoading24h : isLoading7d;

  // Estadísticas
  const stats = useMemo(() => {
    if (period === '24h' && hourlyData.length === 0) return null;
    if (period === '7d' && weeklyData.length === 0) return null;
    
    if (period === '24h') {
      const temps = hourlyData.map(r => r.temperature);
      const hums = hourlyData.map(r => r.humidity);
      
      const current = hourlyData[hourlyData.length - 1];
      const previous = hourlyData[hourlyData.length - 2];
      
      return {
        temp: {
          current: current?.temperature || 0,
          min: Math.min(...temps),
          max: Math.max(...temps),
          avg: Math.round((temps.reduce((a, b) => a + b, 0) / temps.length) * 10) / 10,
          trend: current && previous ? current.temperature - previous.temperature : 0,
        },
        humidity: {
          current: current?.humidity || 0,
          min: Math.min(...hums),
          max: Math.max(...hums),
          avg: Math.round(hums.reduce((a, b) => a + b, 0) / hums.length),
          trend: current && previous ? current.humidity - previous.humidity : 0,
        },
      };
    } else {
      // Estadísticas para 7 días
      const temps = weeklyData.map(d => d.tempAvg);
      const hums = weeklyData.map(d => d.humAvg);
      const current = weeklyData[weeklyData.length - 1];
      const previous = weeklyData[weeklyData.length - 2];
      
      return {
        temp: {
          current: current?.tempAvg || 0,
          min: Math.min(...weeklyData.map(d => d.tempMin)),
          max: Math.max(...weeklyData.map(d => d.tempMax)),
          avg: Math.round((temps.reduce((a, b) => a + b, 0) / temps.length) * 10) / 10,
          trend: current && previous ? current.tempAvg - previous.tempAvg : 0,
        },
        humidity: {
          current: current?.humAvg || 0,
          min: Math.min(...weeklyData.map(d => d.humMin)),
          max: Math.max(...weeklyData.map(d => d.humMax)),
          avg: Math.round(hums.reduce((a, b) => a + b, 0) / hums.length),
          trend: current && previous ? current.humAvg - previous.humAvg : 0,
        },
      };
    }
  }, [period, hourlyData, weeklyData]);

  if (!sensor) return null;

  const getTrendIcon = (trend: number) => {
    if (trend > 0.5) return <TrendingUp className="w-4 h-4 text-success" />;
    if (trend < -0.5) return <TrendingDown className="w-4 h-4 text-destructive" />;
    return <Minus className="w-4 h-4 text-muted-foreground" />;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <History className="w-5 h-5 text-primary" />
            Histórico - {sensor.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {isLoading && (
            <div className="space-y-4">
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-64 w-full" />
            </div>
          )}

          {!isLoading && !stats && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <AlertCircle className="w-12 h-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                No hay datos disponibles para el período seleccionado
              </p>
            </div>
          )}

          {!isLoading && stats && (
            <>
          {/* Period selector */}
          <div className="flex items-center justify-between">
            <div className="flex gap-2">
              <Button
                variant={period === '24h' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setPeriod('24h')}
              >
                Últimas 24h
              </Button>
              <Button
                variant={period === '7d' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setPeriod('7d')}
              >
                Últimos 7 días
              </Button>
            </div>
            <Badge variant="outline" className="gap-1">
              <Calendar className="w-3 h-3" />
              {new Date().toLocaleDateString('es-ES')}
            </Badge>
          </div>

          {/* Stats cards */}
          {stats && (
            <div className="grid grid-cols-2 gap-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Thermometer className="w-4 h-4 text-warning" />
                    Temperatura
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-display font-bold">{stats.temp.current}°C</span>
                    {getTrendIcon(stats.temp.trend)}
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-3 text-sm">
                    <div>
                      <p className="text-muted-foreground text-xs">Mín</p>
                      <p className="font-medium">{stats.temp.min}°C</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs">Media</p>
                      <p className="font-medium">{stats.temp.avg}°C</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs">Máx</p>
                      <p className="font-medium">{stats.temp.max}°C</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Droplets className="w-4 h-4 text-info" />
                    Humedad
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-display font-bold text-info">{stats.humidity.current}%</span>
                    {getTrendIcon(stats.humidity.trend)}
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-3 text-sm">
                    <div>
                      <p className="text-muted-foreground text-xs">Mín</p>
                      <p className="font-medium">{stats.humidity.min}%</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs">Media</p>
                      <p className="font-medium">{stats.humidity.avg}%</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs">Máx</p>
                      <p className="font-medium">{stats.humidity.max}%</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Charts */}
          <Tabs defaultValue="temperature" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="temperature" className="gap-2">
                <Thermometer className="w-4 h-4" />
                Temperatura
              </TabsTrigger>
              <TabsTrigger value="humidity" className="gap-2">
                <Droplets className="w-4 h-4" />
                Humedad
              </TabsTrigger>
              <TabsTrigger value="conductivity" className="gap-2">
                <Zap className="w-4 h-4" />
                Conductividad
              </TabsTrigger>
            </TabsList>

            <TabsContent value="temperature" className="mt-4">
              <Card>
                <CardContent className="pt-4">
                  <div className="h-[250px]">
                    <ResponsiveContainer width="100%" height="100%">
                      {period === '24h' ? (
                        <LineChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                          <XAxis 
                            dataKey="time" 
                            tick={{ fontSize: 10 }}
                            interval={3}
                          />
                          <YAxis 
                            tick={{ fontSize: 10 }}
                            domain={['dataMin - 2', 'dataMax + 2']}
                          />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: 'hsl(var(--card))',
                              border: '1px solid hsl(var(--border))',
                              borderRadius: '8px',
                            }}
                          />
                          <Line 
                            type="monotone" 
                            dataKey="temperatura" 
                            stroke="hsl(var(--warning))" 
                            strokeWidth={2}
                            dot={false}
                            name="Temperatura (°C)"
                          />
                        </LineChart>
                      ) : (
                        <AreaChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                          <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                          <YAxis tick={{ fontSize: 10 }} />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: 'hsl(var(--card))',
                              border: '1px solid hsl(var(--border))',
                              borderRadius: '8px',
                            }}
                          />
                          <Area 
                            type="monotone" 
                            dataKey="tempMax" 
                            stroke="hsl(var(--destructive))" 
                            fill="hsl(var(--destructive) / 0.2)"
                            name="Máxima (°C)"
                          />
                          <Area 
                            type="monotone" 
                            dataKey="tempAvg" 
                            stroke="hsl(var(--warning))" 
                            fill="hsl(var(--warning) / 0.3)"
                            name="Media (°C)"
                          />
                          <Area 
                            type="monotone" 
                            dataKey="tempMin" 
                            stroke="hsl(var(--info))" 
                            fill="hsl(var(--info) / 0.2)"
                            name="Mínima (°C)"
                          />
                        </AreaChart>
                      )}
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="humidity" className="mt-4">
              <Card>
                <CardContent className="pt-4">
                  <div className="h-[250px]">
                    <ResponsiveContainer width="100%" height="100%">
                      {period === '24h' ? (
                        <AreaChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                          <XAxis 
                            dataKey="time" 
                            tick={{ fontSize: 10 }}
                            interval={3}
                          />
                          <YAxis 
                            tick={{ fontSize: 10 }}
                            domain={[0, 100]}
                          />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: 'hsl(var(--card))',
                              border: '1px solid hsl(var(--border))',
                              borderRadius: '8px',
                            }}
                          />
                          <Area 
                            type="monotone" 
                            dataKey="humedad" 
                            stroke="hsl(var(--info))" 
                            fill="hsl(var(--info) / 0.3)"
                            strokeWidth={2}
                            name="Humedad (%)"
                          />
                        </AreaChart>
                      ) : (
                        <AreaChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                          <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                          <YAxis tick={{ fontSize: 10 }} domain={[0, 100]} />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: 'hsl(var(--card))',
                              border: '1px solid hsl(var(--border))',
                              borderRadius: '8px',
                            }}
                          />
                          <Area 
                            type="monotone" 
                            dataKey="humMax" 
                            stroke="hsl(var(--info))" 
                            fill="hsl(var(--info) / 0.2)"
                            name="Máxima (%)"
                          />
                          <Area 
                            type="monotone" 
                            dataKey="humAvg" 
                            stroke="hsl(var(--primary))" 
                            fill="hsl(var(--primary) / 0.3)"
                            name="Media (%)"
                          />
                          <Area 
                            type="monotone" 
                            dataKey="humMin" 
                            stroke="hsl(var(--muted-foreground))" 
                            fill="hsl(var(--muted) / 0.5)"
                            name="Mínima (%)"
                          />
                        </AreaChart>
                      )}
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="conductivity" className="mt-4">
              <Card>
                <CardContent className="pt-4">
                  <div className="h-[250px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={period === '24h' ? chartData : []}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                        <XAxis 
                          dataKey="time" 
                          tick={{ fontSize: 10 }}
                          interval={3}
                        />
                        <YAxis tick={{ fontSize: 10 }} />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))',
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px',
                          }}
                        />
                        <Line 
                          type="monotone" 
                          dataKey="conductividad" 
                          stroke="hsl(var(--accent))" 
                          strokeWidth={2}
                          dot={false}
                          name="Conductividad (µS/cm)"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                  {period === '7d' && (
                    <p className="text-center text-sm text-muted-foreground mt-4">
                      Datos de conductividad solo disponibles para las últimas 24 horas
                    </p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
